import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CollectionsBand } from "../../../components/discovery/CollectionsBand";
import type { DiscoveryTabSelection } from "../../../components/discovery/DiscoveryTabBar";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { SaveableCollectionCard } from "../../../domains/playlist/components/SaveableCollectionCard";
import { useApiInfiniteQuery } from "../../../services/api";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";

export function DesktopCollectionsSection({
  selection,
}: {
  selection: DiscoveryTabSelection;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { contentLang } = useActiveLang();
  const history = useHistory();
  const personalQuery = useApiInfiniteQuery(
    "/webapp/private/playlists/recommended",
    selection.kind === "room"
      ? false
      : {
          durable: true,
          query: {
            limit: 20,
            language: contentLang,
            ...(selection.kind === "topic"
              ? { topicUid: selection.topicUid }
              : {}),
          },
        }
  );
  const roomQuery = useApiInfiniteQuery(
    "/webapp/public/playlists/recommended",
    selection.kind === "room"
      ? {
          durable: true,
          query: { limit: 20, language: contentLang, roomId: selection.roomId },
        }
      : false
  );
  const query = selection.kind === "room" ? roomQuery : personalQuery;
  const title =
    selection.kind === "room"
      ? t("Room collections")
      : selection.kind === "topic"
        ? t("Related collections")
        : t("Collections for you");
  const seeAllTo =
    selection.kind === "room"
      ? `/room/playlists/${selection.roomId}`
      : "/discovery/playlists";

  if (query.state === "pending") {
    return (
      <CollectionsBand
        size="desktop"
        title={title}
        seeAllTo={seeAllTo}
        playlists={[]}
        isLoading={true}
        renderCard={() => null}
      />
    );
  }
  if (query.state === "error") {
    return (
      <p role="alert" className="py-4 text-footnote text-ds-slate-600">
        {query.message}
      </p>
    );
  }
  if (query.state === "idle") return null;

  return (
    <CollectionsBand
      size="desktop"
      title={title}
      seeAllTo={seeAllTo}
      playlists={query.docs.slice(0, 8)}
      isLoading={false}
      renderCard={(playlist) => (
        <SaveableCollectionCard
          playlist={playlist}
          listName={AnalyticsItemList.DISCOVERY_PLAYLISTS}
          onOpen={() => {
            history.push(
              selection.kind === "room"
                ? `/room/playlist/${selection.roomId}/${playlist.id}`
                : `/playlist/public/${playlist.id}`
            );
          }}
        />
      )}
    />
  );
}
